import { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input, Button, Popconfirm, message, Spin } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
  EditOutlined,
  DeleteOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useNotebooks, useAllNotesGrouped, useNotesList, getDefaultNotebookId } from '../hooks/useNotes';
import { useNoteSearch } from '../hooks/useNoteSearch';
import type { Notebook } from '@compendium/shared';

export interface NotesSidebarHandle {
  focusSearch: () => void;
}

interface NotesSidebarProps {
  onNoteSelect?: () => void;
}

const NotesSidebar = forwardRef<NotesSidebarHandle, NotesSidebarProps>(
  function NotesSidebar({ onNoteSelect }, ref) {
    const navigate = useNavigate();
    const { id: activeNoteId } = useParams<{ id: string }>();
    const searchRef = useRef<HTMLInputElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
    const [newNotebookName, setNewNotebookName] = useState('');
    const [showNewNotebook, setShowNewNotebook] = useState(false);
    const [editingNotebook, setEditingNotebook] = useState<Notebook | null>(null);
    const [editName, setEditName] = useState('');

    const { notebooks, isLoading: nbLoading, createNotebook, updateNotebook, deleteNotebook } = useNotebooks();
    const { grouped, isLoading: notesLoading } = useAllNotesGrouped();
    const { createNote } = useNotesList();
    const { results: searchResults, isLoading: searchLoading } = useNoteSearch(searchQuery);

    useImperativeHandle(ref, () => ({
      focusSearch: () => searchRef.current?.focus(),
    }));

    const isSearching = searchQuery.trim().length >= 2;

    function handleNoteClick(noteId: number) {
      navigate(`/reviewer/notes/${noteId}`);
      onNoteSelect?.();
    }

    async function handleNewNote(notebookId: number) {
      try {
        const note = await createNote.mutateAsync({ title: 'Untitled', content: '', notebookId });
        navigate(`/reviewer/notes/${note.id}`);
        onNoteSelect?.();
      } catch {
        message.error('Failed to create note');
      }
    }

    function toggleNotebook(id: number) {
      setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    }

    async function handleCreateNotebook() {
      const name = newNotebookName.trim();
      if (!name) return;
      try {
        await createNotebook.mutateAsync({ name });
        setNewNotebookName('');
        setShowNewNotebook(false);
        message.success('Notebook created');
      } catch {
        message.error('Failed to create notebook');
      }
    }

    async function handleUpdateNotebook() {
      if (!editingNotebook) return;
      const name = editName.trim();
      if (!name || name === editingNotebook.name) {
        setEditingNotebook(null);
        return;
      }
      try {
        await updateNotebook.mutateAsync({ id: editingNotebook.id, data: { name } });
        setEditingNotebook(null);
        message.success('Notebook renamed');
      } catch {
        message.error('Failed to rename notebook');
      }
    }

    const loading = nbLoading || notesLoading;

    return (
      <div className="notes-sidebar">
        {/* Search */}
        <div style={{ padding: '12px 12px 8px' }}>
          <Input
            ref={searchRef as any}
            placeholder="Search notes..."
            prefix={<SearchOutlined style={{ color: 'var(--text-muted)' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            size="small"
            style={{ marginBottom: 8 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            block
            onClick={() => {
              const defaultId = getDefaultNotebookId(notebooks);
              if (defaultId) {
                handleNewNote(defaultId);
              } else {
                setShowNewNotebook(true);
              }
            }}
          >
            New Note
          </Button>
        </div>

        {/* Content area */}
        <div className="notes-sidebar-content">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><Spin size="small" /></div>
          ) : isSearching ? (
            /* Search results */
            <div>
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}><Spin size="small" /></div>
              ) : searchResults.length === 0 ? (
                <div style={{ padding: '16px 12px', color: 'var(--text-muted)', fontSize: 13 }}>No results</div>
              ) : (
                searchResults.map((r) => (
                  <div
                    key={r.note.id}
                    className={`sidebar-note-item ${String(r.note.id) === activeNoteId ? 'active' : ''}`}
                    onClick={() => handleNoteClick(r.note.id)}
                  >
                    <div
                      className="note-title"
                      dangerouslySetInnerHTML={{ __html: r.titleSnippet }}
                    />
                    <div className="note-meta">{r.notebookName}</div>
                    {r.contentSnippet && (
                      <div
                        className="note-snippet"
                        dangerouslySetInnerHTML={{ __html: r.contentSnippet }}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Notebook tree */
            <div>
              {notebooks.map((nb) => {
                const nbNotes = grouped[nb.id] ?? [];
                const isCollapsed = collapsed[nb.id] ?? false;

                return (
                  <div key={nb.id} className="sidebar-notebook">
                    {/* Notebook header */}
                    <div className="notebook-header" onClick={() => toggleNotebook(nb.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
                        {isCollapsed ? (
                          <RightOutlined style={{ fontSize: 9, color: 'var(--text-muted)' }} />
                        ) : (
                          <DownOutlined style={{ fontSize: 9, color: 'var(--text-muted)' }} />
                        )}
                        {editingNotebook?.id === nb.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onPressEnter={handleUpdateNotebook}
                            onBlur={handleUpdateNotebook}
                            autoFocus
                            size="small"
                            style={{ width: 120 }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="notebook-name">{nb.name}</span>
                        )}
                        <span className="notebook-count">{nbNotes.length}</span>
                      </div>
                      <div className="notebook-actions" onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="text"
                          size="small"
                          icon={<PlusOutlined style={{ fontSize: 11 }} />}
                          onClick={() => handleNewNote(nb.id)}
                          style={{ color: 'var(--text-muted)', width: 22, height: 22 }}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined style={{ fontSize: 11 }} />}
                          onClick={() => { setEditingNotebook(nb); setEditName(nb.name); }}
                          style={{ color: 'var(--text-muted)', width: 22, height: 22 }}
                        />
                        <Popconfirm
                          title="Delete notebook?"
                          description="All notes will be deleted."
                          onConfirm={() => deleteNotebook.mutateAsync(nb.id).then(() => message.success('Deleted'))}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                            danger
                            style={{ width: 22, height: 22 }}
                          />
                        </Popconfirm>
                      </div>
                    </div>

                    {/* Notes list */}
                    {!isCollapsed && nbNotes.map((note) => (
                      <div
                        key={note.id}
                        className={`sidebar-note-item ${String(note.id) === activeNoteId ? 'active' : ''}`}
                        onClick={() => handleNoteClick(note.id)}
                      >
                        <div className="note-title">{note.title || 'Untitled'}</div>
                        <div className="note-meta">
                          {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
                        </div>
                      </div>
                    ))}

                    {!isCollapsed && nbNotes.length === 0 && (
                      <div style={{ padding: '8px 12px 8px 28px', color: 'var(--text-muted)', fontSize: 12 }}>
                        No notes
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add notebook */}
              {showNewNotebook ? (
                <div style={{ padding: '8px 12px', display: 'flex', gap: 4 }}>
                  <Input
                    placeholder="Notebook name"
                    value={newNotebookName}
                    onChange={(e) => setNewNotebookName(e.target.value)}
                    onPressEnter={handleCreateNotebook}
                    autoFocus
                    size="small"
                  />
                  <Button size="small" type="primary" onClick={handleCreateNotebook}>
                    Add
                  </Button>
                </div>
              ) : (
                <div style={{ padding: '8px 12px' }}>
                  <Button
                    type="dashed"
                    size="small"
                    block
                    icon={<SettingOutlined />}
                    onClick={() => setShowNewNotebook(true)}
                    style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
                  >
                    New Notebook
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
          .notes-sidebar {
            display: flex;
            flex-direction: column;
            height: 100%;
            background: var(--bg-base);
          }
          .notes-sidebar-content {
            flex: 1;
            overflow-y: auto;
          }
          .sidebar-notebook {
            border-bottom: 1px solid var(--border-subtle);
          }
          .notebook-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            cursor: pointer;
            user-select: none;
          }
          .notebook-header:hover {
            background: var(--bg-elevated);
          }
          .notebook-name {
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--text-secondary);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .notebook-count {
            font-size: 10px;
            color: var(--text-muted);
            margin-left: 4px;
          }
          .notebook-actions {
            display: flex;
            gap: 0;
            opacity: 0;
            transition: opacity 0.15s;
          }
          .notebook-header:hover .notebook-actions {
            opacity: 1;
          }
          .sidebar-note-item {
            padding: 8px 12px 8px 28px;
            cursor: pointer;
            transition: background 0.1s;
            border-left: 3px solid transparent;
          }
          .sidebar-note-item:hover {
            background: var(--bg-surface);
          }
          .sidebar-note-item.active {
            background: var(--accent-gold-tint);
            border-left-color: var(--accent-gold);
          }
          .sidebar-note-item .note-title {
            font-size: 13px;
            color: #e0e0e0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .sidebar-note-item.active .note-title {
            color: #fff;
          }
          .sidebar-note-item .note-meta {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 1px;
          }
          .sidebar-note-item .note-snippet {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .sidebar-note-item .note-snippet mark {
            background: var(--accent-gold-tint2);
            color: #fff;
          }
          .sidebar-note-item .note-title mark {
            background: var(--accent-gold-tint2);
            color: #fff;
          }
        `}</style>
      </div>
    );
  }
);

export default NotesSidebar;
